import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, SITE_URL } from '../../hooks/useSEO';

const ManagedServicesPage = () => {
  useSEO({
    title: 'Managed Cybersecurity Services for Mortgage Companies | Mortgage Moat',
    description: 'Get 24/7 managed cyber protection for your mortgage firm. Includes phishing simulations, endpoint security, SOC monitoring, and GLBA compliance — starting at $20/user/month.',
    keywords: 'managed cybersecurity services, mortgage company security, 24/7 security monitoring, GLBA compliance, phishing simulation',
    canonical: '/managed-services',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: '24/7 Managed Cyber Protection',
        provider: { '@type': 'Organization', name: 'Mortgage Moat', url: SITE_URL },
        url: `${SITE_URL}/managed-services`,
        description: 'Comprehensive managed security services including 24/7 monitoring, phishing simulations, endpoint protection, and incident response for mortgage companies.',
        offers: {
          '@type': 'Offer',
          price: '20',
          priceCurrency: 'USD',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '20',
            priceCurrency: 'USD',
            unitText: 'user/month',
          },
        },
        areaServed: { '@type': 'Country', name: 'United States' },
        serviceType: 'Managed Security Service',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Managed Services', item: `${SITE_URL}/managed-services` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How quickly can we get started with managed services?',
            acceptedAnswer: { '@type': 'Answer', text: 'Most clients are fully onboarded within 48-72 hours. We handle all the technical setup – you just need to provide access to your systems.' },
          },
          {
            '@type': 'Question',
            name: 'Is there a long-term contract for managed services?',
            acceptedAnswer: { '@type': 'Answer', text: 'No. Our managed services are month-to-month with no long-term commitment required. However, we offer discounts for annual subscriptions.' },
          },
          {
            '@type': 'Question',
            name: 'What happens if there\'s a security incident?',
            acceptedAnswer: { '@type': 'Answer', text: 'Our incident response team is available 24/7. We\'ll immediately contain the threat, investigate the incident, and guide you through remediation. All incident response is included in your subscription.' },
          },
          {
            '@type': 'Question',
            name: 'How do the phishing simulations work?',
            acceptedAnswer: { '@type': 'Answer', text: 'We send realistic phishing emails to your employees monthly. Those who click receive immediate training. You get detailed reports showing who\'s at risk and how your team\'s security awareness improves over time.' },
          },
        ],
      },
    ],
  });

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: '24/7 Security Monitoring',
      description: 'Round-the-clock surveillance of your systems, networks, and endpoints with real-time threat detection'
    },
    {
      icon: 'ri-mail-check-line',
      title: 'Continuous Phishing Simulations',
      description: 'Automated phishing tests sent to your team monthly, with detailed reporting and targeted training'
    },
    {
      icon: 'ri-alert-line',
      title: 'Instant Threat Response',
      description: 'Immediate action on detected threats with expert incident response and containment'
    },
    {
      icon: 'ri-file-shield-2-line',
      title: 'Vulnerability Management',
      description: 'Regular scanning and patching of security vulnerabilities across all your systems'
    },
    {
      icon: 'ri-dashboard-3-line',
      title: 'Security Dashboard Portal',
      description: 'Real-time visibility into your security posture with comprehensive reporting and analytics'
    },
    {
      icon: 'ri-user-settings-line',
      title: 'Dedicated Security Team',
      description: 'Your own team of cybersecurity experts available whenever you need them'
    }
  ];

  const included = [
    {
      category: 'Monitoring & Protection',
      items: [
        'Network security monitoring',
        'Endpoint detection and response',
        'Email security gateway',
        'Web filtering and protection',
        'Dark web monitoring',
        'Threat intelligence feeds'
      ]
    },
    {
      category: 'Testing & Training',
      items: [
        'Monthly phishing simulations',
        'Quarterly security awareness training',
        'Social engineering tests',
        'Employee security scorecards',
        'Custom training modules',
        'Compliance training tracking'
      ]
    },
    {
      category: 'Management & Reporting',
      items: [
        'Security operations center (SOC)',
        'Incident response team',
        'Monthly executive reports',
        'Compliance documentation',
        'Vendor risk management',
        'Policy updates and maintenance'
      ]
    }
  ];

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cost-Effective',
      description: 'Get enterprise-level security for a fraction of the cost of building an in-house team'
    },
    {
      icon: 'ri-team-line',
      title: 'Expert Team',
      description: 'Access to certified security professionals with mortgage industry expertise'
    },
    {
      icon: 'ri-time-line',
      title: 'Save Time',
      description: 'Focus on your business while we handle all your security needs'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Stay Compliant',
      description: 'Maintain GLBA, FFIEC, and other regulatory compliance automatically'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 to-red-800 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold mb-6">
              MANAGED CYBER SERVICES PORTAL
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Don't Have a Security Team? We'll Be Yours
            </h1>
            <p className="text-xl mb-8 text-white/90">
              Comprehensive managed security services that protect your company from every angle – automatically, around the clock.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/purchase/managed-services"
                className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Started
                <i className="ri-arrow-right-line"></i>
              </a>
              <a 
                href="/schedule-call"
                className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                View Demo
                <i className="ri-play-circle-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Everything You Need to Stay Secure
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our managed services portal provides comprehensive protection with continuous monitoring, testing, and expert support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-xl mb-6">
                  <i className={`${feature.icon} text-3xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What's Included in Your Subscription
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A complete security program delivered through our managed services portal.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {included.map((section, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 pb-4 border-b-2 border-red-600">
                  {section.category}
                </h3>
                <ul className="space-y-3">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-green-500 text-xl"></i>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Managed Services?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-6">
                  <i className={`${benefit.icon} text-4xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Phishing Simulation Highlight */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div className="p-12 flex flex-col justify-center">
                <div className="inline-block bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-bold mb-6 self-start">
                  INCLUDED IN SUBSCRIPTION
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Continuous Phishing Simulations
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Your employees are your first line of defense. Our automated phishing simulations test your team monthly, identify vulnerabilities, and provide targeted training to those who need it most.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Monthly automated phishing tests',
                    'Real-world attack scenarios',
                    'Individual employee tracking',
                    'Automatic remedial training',
                    'Detailed reporting and analytics',
                    'Compliance documentation'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-green-500 text-xl"></i>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative h-96 lg:h-auto">
                <img 
                  src="https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/45d9a998ef2d8c1fc747fae2da1161f7.png"
                  alt="Phishing Simulation Monitoring"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Enterprise-level security at a price small businesses can afford
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-2xl shadow-2xl overflow-hidden text-white">
            <div className="text-center py-8 bg-black/20">
              <div className="text-sm font-bold uppercase tracking-wide mb-2">Per User Pricing</div>
              <div className="text-6xl font-bold mb-2">$20<span className="text-2xl">/user/month</span></div>
              <div className="text-xl opacity-90">
                Scalable protection that grows with your team
              </div>
            </div>
            
            <div className="p-12">
              <h3 className="text-3xl font-bold mb-8 text-center">
                Complete Managed Security Package
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-10">
                {[
                  '24/7 security monitoring per user',
                  'Endpoint protection',
                  'Email security gateway',
                  'Monthly phishing simulations',
                  'Quarterly security training',
                  'Vulnerability scanning & patching',
                  'Incident response team',
                  'Security operations center (SOC)',
                  'Real-time threat alerts',
                  'Monthly executive reports',
                  'Compliance documentation',
                  'Dedicated security portal',
                  'Dark web monitoring',
                  'Vendor risk management',
                  'Policy updates & maintenance',
                  'Unlimited support'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-xl"></i>
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8">
                <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <i className="ri-device-line text-2xl"></i>
                  Flexible Device Scaling
                </h4>
                <p className="text-white/90 mb-4">
                  Need to protect more than one device per user? We've got you covered. Add additional devices to any user's protection plan with affordable scaling options.
                </p>
                <ul className="space-y-2 text-white/90">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-white mt-1"></i>
                    <span>Scale device count per user as needed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-white mt-1"></i>
                    <span>Maintain affordability with volume pricing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-white mt-1"></i>
                    <span>Perfect for users with multiple workstations</span>
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t border-white/20">
                  <p className="text-sm text-white/75">
                    Contact us for custom device pricing based on your specific needs
                  </p>
                </div>
              </div>

              <div className="text-center">
                <a 
                  href="/purchase/managed-services"
                  className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Subscribe Now
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              <strong>Volume discounts available:</strong> 10% off for 20-49 users • 15% off for 50-99 users • Custom pricing for 100+ users
            </p>
            <a 
              href="/schedule-call"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-6 py-3 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Request Custom Quote
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            {[
              {
                question: 'How quickly can we get started?',
                answer: 'Most clients are fully onboarded within 48-72 hours. We handle all the technical setup – you just need to provide access to your systems.'
              },
              {
                question: 'What if we already have some security tools?',
                answer: 'We can integrate with your existing security infrastructure or replace it entirely. We\'ll assess your current setup and recommend the best approach during onboarding.'
              },
              {
                question: 'Is there a long-term contract?',
                answer: 'No. Our managed services are month-to-month with no long-term commitment required. However, we offer discounts for annual subscriptions.'
              },
              {
                question: 'What happens if there\'s a security incident?',
                answer: 'Our incident response team is available 24/7. We\'ll immediately contain the threat, investigate the incident, and guide you through remediation. All incident response is included in your subscription.'
              },
              {
                question: 'How do the phishing simulations work?',
                answer: 'We send realistic phishing emails to your employees monthly. Those who click receive immediate training. You get detailed reports showing who\'s at risk and how your team\'s security awareness improves over time.'
              },
              {
                question: 'Can we customize the service to our needs?',
                answer: 'Absolutely. While our standard package covers most mortgage companies, we can customize monitoring rules, training content, and reporting to match your specific requirements.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Stop Worrying About Cybersecurity
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Let our experts handle your security while you focus on growing your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/purchase/managed-services"
              className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Start Your Subscription
              <i className="ri-arrow-right-line"></i>
            </a>
            <a 
              href="/schedule-call"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Schedule a Demo
              <i className="ri-calendar-line"></i>
            </a>
          </div>
          <p className="text-sm text-white/75 mt-6">
            Questions? Call us at (555) 123-4567 or email info@mortgagemoat.com
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ManagedServicesPage;